import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { RefObject } from 'react';
import { TimelinePanel } from './TimelinePanel';

interface TimelineDrawerProps {
  open: boolean;
  onClose: () => void;
  scrollRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Slide-over timeline overlay used at narrow viewport widths
 * (< 1200px), where the inline timeline panel is hidden by default.
 *
 * Scoped to the Track view content area: positioned absolutely against
 * its nearest positioned ancestor (the Track view's `relative` wrapper),
 * so the app sidebar/header stay visible.
 *
 * Dismissed by Esc, clicking the backdrop, or the toggle button in the
 * Track view header.
 */
export function TimelineDrawer({ open, onClose, scrollRef }: TimelineDrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="timeline-drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            className="absolute inset-0 z-30 bg-black/35 backdrop-blur-[1px]"
            aria-hidden="true"
            data-testid="timeline-drawer-backdrop"
          />
          <motion.div
            key="timeline-drawer-panel"
            role="dialog"
            aria-label="Timeline"
            initial={{ x: 460, opacity: 0.6 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 460, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.25, 1, 0.5, 1] }}
            className="absolute right-3 top-3 bottom-3 z-40 w-[440px] max-w-[calc(100%-1.5rem)] shadow-2xl shadow-black/40 rounded-[10px] overflow-hidden"
            data-testid="timeline-drawer-panel"
          >
            <TimelinePanel scrollRef={scrollRef} />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
